import React from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';

export interface FilterSectionState {
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  search?: string;
}

export interface FilterSectionStatusOption {
  value: string;
  label: string;
}

export interface FilterSectionProps {
  /** Current filter values */
  filters: FilterSectionState;
  /** Callback when any filter changes. Pass partial state; implementation should reset page to 0. */
  onFilterChange: (partial: Partial<FilterSectionState>) => void;
  /** Callback to clear all filters */
  onClear: () => void;
  /** Show date range (Date from / Date to) */
  showDateRange?: boolean;
  /** Show status dropdown */
  showStatus?: boolean;
  /** Status options for dropdown. First option should be "All" with value "". */
  statusOptions?: FilterSectionStatusOption[];
  /** Show search / AI search field */
  showSearch?: boolean;
  /** Label for search field (e.g. "Search" or "AI search") */
  searchLabel?: string;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Extra content (e.g. custom filters) after main filters, before Clear button */
  children?: React.ReactNode;
}

const defaultStatusOptions: FilterSectionStatusOption[] = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED_BY_TEACHER', label: 'Approved by teacher' },
  { value: 'APPROVED_BY_PRINCIPAL', label: 'Approved by principal' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected by School Staff' },
  { value: 'CANCELLED', label: 'Cancelled By You' },
];

const FilterSection: React.FC<FilterSectionProps> = ({
  filters,
  onFilterChange,
  onClear,
  showDateRange = true,
  showStatus = true,
  statusOptions = defaultStatusOptions,
  showSearch = true,
  searchLabel = 'Search',
  searchPlaceholder = 'Search by student name or notes…',
  children,
}) => {
  const hasAnyFilter =
    (filters.dateFrom && filters.dateFrom !== '') ||
    (filters.dateTo && filters.dateTo !== '') ||
    (filters.status && filters.status !== '') ||
    (filters.search && filters.search.trim() !== '');

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}> <FilterListIcon sx={{ fontSize: 20 }} /> Filter by:</Typography>
      {showDateRange && (
        <>
          <TextField
            size="small"
            type="date"
            label="Date from"
            value={filters.dateFrom ?? ''}
            onChange={(e) => onFilterChange({ dateFrom: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            size="small"
            type="date"
            label="Date to"
            value={filters.dateTo ?? ''}
            onChange={(e) => onFilterChange({ dateTo: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
        </>
      )}
      {showStatus && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filters.status ?? ''}
            onChange={(e) => onFilterChange({ status: e.target.value })}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {showSearch && (
        <TextField
          size="small"
          label={searchLabel}
          placeholder={searchPlaceholder}
          value={filters.search ?? ''}
          onChange={(e) => onFilterChange({ search: e.target.value })}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'action.active' }} /> }}
          sx={{ minWidth: 220 }}
        />
      )}
      {children}
      {hasAnyFilter && (
        <Button variant="outlined" size="small" onClick={onClear}>
          Clear filters
        </Button>
      )}
    </Box>
  );
};

export default FilterSection;
